import { Player } from '../types';

/**
 * Distribuye desafíos de manera GLOBALMENTE equitativa
 * Usa una matriz de distribución determi​nística para garantizar que cada jugador reciba exactamente la misma cantidad
 */
export function distributeEquitablyChallenges(
  players: Player[],
  currentPlayerId: string,
  challengesPerPlayer: number = 3
): Player[] {
  // Excluir al jugador actual (no puede escribir desafíos sobre sí mismo)
  const otherPlayers = players.filter(p => p.id !== currentPlayerId);
  
  if (otherPlayers.length === 0) {
    console.warn('⚠️ No other players found for challenge distribution');
    return [];
  }

  console.log('🎯 Creating GLOBAL equitable distribution:', {
    totalPlayers: players.length,
    otherPlayers: otherPlayers.length,
    challengesPerPlayer,
    currentPlayer: currentPlayerId.slice(-4)
  });

  // Crear matriz de distribución global determi​nística
  const globalMatrix = createGlobalDistributionMatrix(players, challengesPerPlayer);
  
  // Encontrar el índice del jugador actual
  const currentPlayerIndex = players.findIndex(p => p.id === currentPlayerId);
  if (currentPlayerIndex === -1) {
    console.error('❌ Current player not found in players array');
    return [];
  }
  
  // Extraer la fila correspondiente al jugador actual (sus asignaciones)
  const myAssignments = globalMatrix[currentPlayerIndex];
  
  console.log('✅ Assignments for current player:', {
    playerIndex: currentPlayerIndex,
    assignments: myAssignments.map(targetIndex => players[targetIndex].nickname),
    assignmentCounts: getDistributionCountsFromIndices(myAssignments, players)
  });
  
  // Convertir índices a objetos Player
  return myAssignments.map(targetIndex => players[targetIndex]);
}

/**
 * Crea una matriz de distribución global determi​nística que garantiza equidad perfecta
 */
function createGlobalDistributionMatrix(
  players: Player[],
  challengesPerPlayer: number
): number[][] {
  const numPlayers = players.length;
  const totalChallenges = numPlayers * challengesPerPlayer;
  const challengesPerTarget = Math.floor(totalChallenges / numPlayers); // Cada jugador debe recibir exactamente esto
  
  console.log('🧮 Global matrix calculation:', {
    numPlayers,
    challengesPerPlayer,
    totalChallenges,
    challengesPerTarget,
    isExactlyDivisible: totalChallenges % numPlayers === 0
  });
  
  // Verificar que la distribución es matemáticamente posible
  if (totalChallenges % numPlayers !== 0) {
    console.warn('⚠️ Perfect distribution not possible with current numbers');
  }
  
  // Inicializar matriz
  const matrix: number[][] = [];
  for (let i = 0; i < numPlayers; i++) {
    matrix[i] = [];
  }
  
  // Contador para cuántos desafíos ha recibido cada jugador
  const receivedCount: number[] = new Array(numPlayers).fill(0);
  
  // Algoritmo de distribución round-robin con balance automático
  for (let author = 0; author < numPlayers; author++) {
    for (let challengeIndex = 0; challengeIndex < challengesPerPlayer; challengeIndex++) {
      // Encontrar el jugador que menos desafíos ha recibido (excluyendo al autor)
      let bestTarget = -1;
      let minReceived = Infinity;
      
      for (let target = 0; target < numPlayers; target++) {
        if (target === author) continue; // No puede escribir sobre sí mismo
        
        if (receivedCount[target] < minReceived) {
          minReceived = receivedCount[target];
          bestTarget = target;
        }
      }
      
      if (bestTarget === -1) {
        console.error('❌ Could not find valid target for challenge assignment');
        continue;
      }
      
      // Asignar desafío
      matrix[author].push(bestTarget);
      receivedCount[bestTarget]++;
    }
  }
  
  console.log('📊 Final global distribution matrix:', {
    receivedCounts: receivedCount,
    minReceived: Math.min(...receivedCount),
    maxReceived: Math.max(...receivedCount),
    isBalanced: Math.max(...receivedCount) - Math.min(...receivedCount) <= 1,
    matrixPreview: matrix.slice(0, 3).map(row => row.map(i => players[i].nickname.slice(0, 4)))
  });
  
  return matrix;
}

/**
 * Cuenta distribución desde índices para debugging
 */
function getDistributionCountsFromIndices(indices: number[], players: Player[]): Record<string, number> {
  const counts: Record<string, number> = {};
  
  for (const index of indices) {
    const playerName = players[index].nickname;
    counts[playerName] = (counts[playerName] || 0) + 1;
  }
  
  return counts;
}

// Unused utility functions removed to clean up TypeScript warnings

/**
 * Verifica que la distribución global sea equitativa para todos los jugadores
 * Esta función debe ser llamada después de que todos los jugadores hayan escrito sus desafíos
 */
export function validateGlobalDistribution(
  allChallenges: Array<{ targetPlayerId: string; authorId: string }>,
  allPlayers: Player[]
): { isValid: boolean; report: Record<string, number> } {
  const targetCounts: Record<string, number> = {};
  
  // Inicializar contadores
  for (const player of allPlayers) {
    targetCounts[player.id] = 0;
  }
  
  // Contar desafíos por jugador objetivo
  for (const challenge of allChallenges) {
    if (targetCounts.hasOwnProperty(challenge.targetPlayerId)) {
      targetCounts[challenge.targetPlayerId]++;
    }
  }
  
  const counts = Object.values(targetCounts);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);
  const isValid = maxCount - minCount <= 1; // Permitir diferencia máxima de 1
  
  // Crear reporte legible
  const report: Record<string, number> = {};
  for (const player of allPlayers) {
    report[player.nickname] = targetCounts[player.id];
  }
  
  console.log('🔍 Global distribution validation:', {
    isValid,
    minCount,
    maxCount,
    difference: maxCount - minCount,
    report
  });
  
  return { isValid, report };
}